import { isSameSymbol } from '@/entities/symbol';
import { HIGHLIGHTED_PRICE_MAX_ITEMS } from '@/features/dashboard/constants/const';
import type {
  MarketApp,
  MarketApps,
  MarketAppWithTabs,
  TilesWidgetV2
} from '@/features/dashboard/types';
import { generateUniqueUUID } from '@/shared/lib/format/generateUniqueUUID';

function extractIds<T extends { id: string }>(items: T[]): Set<string> {
  return new Set(items.map(item => item.id));
}

type SelectableWidgetDefinition = { name?: string } & Record<string, unknown>;

export type SelectableWidget = { type: string; definition: SelectableWidgetDefinition };

const isTilesWidget = (app: MarketApp): app is TilesWidgetV2 => {
  return 'type' in app && app.type === 'tiles';
};

export function buildImportedAppFromWidget(
  selectedWidget: SelectableWidget,
  dashboardConfig: MarketApp[]
): MarketApp {
  if (selectedWidget.type === 'tiles') {
    const existingTiles = dashboardConfig.flatMap(app => {
      if (isTilesWidget(app)) {
        return app.tiles.map(tile => tile);
      }
      return [];
    });
    const existingTileIds = extractIds(existingTiles);

    const tilesApp = selectedWidget.definition as unknown as TilesWidgetV2;
    tilesApp.tiles = tilesApp.tiles.map(tile => {
      const newTileId = generateUniqueUUID(existingTileIds);
      existingTileIds.add(newTileId);

      return {
        ...tile,
        id: newTileId
      };
    });
    return tilesApp;
  }

  const appsWithTabs = dashboardConfig.filter(app => 'tabs' in app);

  const existingTabs = appsWithTabs.flatMap(app => app.tabs.map(tab => tab));
  const existingTabIds = extractIds(existingTabs);
  const newTabId = generateUniqueUUID(existingTabIds);

  const existingAppIds = extractIds(appsWithTabs);
  const newAppId = generateUniqueUUID(existingAppIds);

  let name = selectedWidget.definition?.name;
  if (!name && selectedWidget.type === 'feed') {
    name = 'Feed';
  }
  if (!name && selectedWidget.type === 'file_list') {
    name = 'Data lake files';
  }
  return {
    tabs: [{ ...selectedWidget.definition, name, id: newTabId }],
    id: newAppId
  } as MarketApp;
}

export type ComputeNextAppsResult = {
  apps: MarketApps;
  exceeded: boolean;
  limitedCount: number;
  duplicatesCount: number;
  addedCodes: string[];
  duplicateCodes: string[];
};

export function computeNextApps(
  dashboardConfig: MarketApps | undefined,
  importedApp: MarketApp
): ComputeNextAppsResult {
  if (!dashboardConfig || dashboardConfig.length === 0) {
    // Handle tiles widget with limit when dashboard is empty
    if (isTilesWidget(importedApp)) {
      const exceeded = importedApp.tiles.length > HIGHLIGHTED_PRICE_MAX_ITEMS;
      const limitedTiles = importedApp.tiles.slice(0, HIGHLIGHTED_PRICE_MAX_ITEMS);
      const addedCodes = limitedTiles.map(tile => tile.symbol.key.Code);

      const limitedWidget = {
        ...importedApp,
        tiles: limitedTiles
      } as MarketApp;

      return {
        apps: [limitedWidget],
        exceeded,
        limitedCount: limitedTiles.length,
        duplicatesCount: 0,
        addedCodes,
        duplicateCodes: []
      };
    }

    return {
      apps: [importedApp],
      exceeded: false,
      limitedCount: 0,
      duplicatesCount: 0,
      addedCodes: [],
      duplicateCodes: []
    };
  }

  if (isTilesWidget(importedApp)) {
    const current = [...dashboardConfig];
    const existingTilesIndex = current.findIndex(isTilesWidget);

    if (existingTilesIndex !== -1) {
      const existing = current[existingTilesIndex];
      if (!isTilesWidget(existing)) {
        return {
          apps: dashboardConfig,
          exceeded: false,
          limitedCount: 0,
          duplicatesCount: 0,
          addedCodes: [],
          duplicateCodes: []
        };
      }

      // Remove duplicates based on symbol equality
      const existingSymbols = existing.tiles.map(tile => tile.symbol.key);

      const newTiles = importedApp.tiles.filter(
        tile => !existingSymbols.some(es => isSameSymbol(es, tile.symbol.key))
      );

      // Get duplicate codes
      const duplicateTiles = importedApp.tiles.filter(tile =>
        existingSymbols.some(es => isSameSymbol(es, tile.symbol.key))
      );
      const duplicateCodes = duplicateTiles.map(tile => tile.symbol.key.Code);

      // Count duplicates
      const duplicatesCount = importedApp.tiles.length - newTiles.length;

      // Check if we'll exceed the limit
      const totalTiles = existing.tiles.length + newTiles.length;
      const exceeded = totalTiles > HIGHLIGHTED_PRICE_MAX_ITEMS;

      // Limit to HIGHLIGHTED_PRICE_MAX_ITEMS
      const availableSlots = HIGHLIGHTED_PRICE_MAX_ITEMS - existing.tiles.length;
      const tilesToAdd = newTiles.slice(0, Math.max(0, availableSlots));
      const addedCodes = tilesToAdd.map(tile => tile.symbol.key.Code);

      const merged = {
        ...existing,
        tiles: [...existing.tiles, ...tilesToAdd]
      } as MarketApp;

      const next = [...current];
      next.splice(existingTilesIndex, 1);

      return {
        apps: [merged, ...next],
        exceeded,
        limitedCount: tilesToAdd.length,
        duplicatesCount,
        addedCodes,
        duplicateCodes
      };
    }

    // New tiles widget - limit to HIGHLIGHTED_PRICE_MAX_ITEMS
    const exceeded = importedApp.tiles.length > HIGHLIGHTED_PRICE_MAX_ITEMS;
    const limitedTiles = importedApp.tiles.slice(0, HIGHLIGHTED_PRICE_MAX_ITEMS);
    const addedCodes = limitedTiles.map(tile => tile.symbol.key.Code);

    const limitedWidget = {
      ...importedApp,
      tiles: limitedTiles
    } as MarketApp;

    return {
      apps: [limitedWidget, ...dashboardConfig],
      exceeded,
      limitedCount: limitedTiles.length,
      duplicatesCount: 0,
      addedCodes,
      duplicateCodes: []
    };
  }

  return {
    apps: [...dashboardConfig, importedApp],
    exceeded: false,
    limitedCount: 0,
    duplicatesCount: 0,
    addedCodes: [],
    duplicateCodes: []
  };
}

type TabsWidget = MarketAppWithTabs;

type EditActions = {
  addTile: (tabId: string, tile: TilesWidgetV2['tiles'][number]) => void;
  addApp: (tabId: string, app: MarketAppWithTabs) => void;
  addTabToApp: (tabId: string, appId: string, tab: MarketAppWithTabs['tabs'][number]) => void;
};

type ShowErrorSnackbar = (message: string, autoHideDuration?: number, key?: string) => string;

type ShowSuccessSnackbar = (message: string, key?: string) => string;

const isTabsWidget = (widget: MarketApp): widget is TabsWidget =>
  !('type' in widget) && Array.isArray(widget.tabs);

const buildSymbolKeyId = (key: { Code: string; Period: string; PeriodType: string }) => {
  return `${key.Code}|${key.Period}|${key.PeriodType}`;
};

const computeTileImportOutcome = (
  currentTiles: TilesWidgetV2['tiles'],
  incomingTiles: TilesWidgetV2['tiles']
) => {
  const existingKeys = new Set(currentTiles.map(tile => buildSymbolKeyId(tile.symbol.key)));
  const newTiles: TilesWidgetV2['tiles'] = [];
  const duplicateTiles: TilesWidgetV2['tiles'] = [];

  for (const tile of incomingTiles) {
    if (existingKeys.has(buildSymbolKeyId(tile.symbol.key))) {
      duplicateTiles.push(tile);
    } else {
      newTiles.push(tile);
    }
  }

  const availableSlots = Math.max(0, HIGHLIGHTED_PRICE_MAX_ITEMS - currentTiles.length);
  const tilesToAdd = newTiles.slice(0, availableSlots);
  const exceeded = currentTiles.length + newTiles.length > HIGHLIGHTED_PRICE_MAX_ITEMS;

  return {
    duplicatesCount: duplicateTiles.length,
    duplicateCodes: duplicateTiles.map(tile => tile.symbol.key.Code),
    tilesToAdd,
    exceeded
  };
};

export const buildSuccessMessage = (mutationResult: {
  duplicatesCount: number;
  addedCodes: string[];
}) => {
  if (mutationResult.duplicatesCount === 0 && mutationResult.addedCodes.length === 0) {
    return 'Dashboard updated!';
  }

  const parts: string[] = [];
  if (mutationResult.duplicatesCount > 0) {
    parts.push(
      `${mutationResult.duplicatesCount} item${mutationResult.duplicatesCount > 1 ? 's' : ''} already in Dashboard`
    );
  }
  if (mutationResult.addedCodes.length > 0) {
    parts.push(
      `${mutationResult.addedCodes.length} item${mutationResult.addedCodes.length > 1 ? 's' : ''} added!`
    );
  }
  return parts.join(', ');
};

const addTilesToDashboard = (
  actions: EditActions,
  currentTabId: string,
  tiles: TilesWidgetV2['tiles']
) => {
  tiles.forEach(tile => {
    actions.addTile(currentTabId, tile);
  });
};

const notifyDuplicateTiles = (
  outcome: ReturnType<typeof computeTileImportOutcome>,
  showErrorSnackbar: ShowErrorSnackbar
) => {
  if (outcome.duplicatesCount === 0) return;

  const duplicatesLabel = `Duplicate Highlighted price${outcome.duplicatesCount > 1 ? 's' : ''}`;
  showErrorSnackbar(
    `${duplicatesLabel} skipped: ${outcome.duplicateCodes.join(', ')}`,
    5000,
    'tiles-duplicates-error'
  );
};

const notifyAddedTiles = (
  outcome: ReturnType<typeof computeTileImportOutcome>,
  showSuccessSnackbar: ShowSuccessSnackbar
) => {
  if (outcome.tilesToAdd.length === 0) return;

  const addedCodes = outcome.tilesToAdd.map(tile => tile.symbol.key.Code);
  const addedLabel = `Highlighted price${addedCodes.length > 1 ? 's' : ''}`;
  showSuccessSnackbar(`${addedLabel} added: ${addedCodes.join(', ')}`);
};

const notifyExceededTiles = (
  outcome: ReturnType<typeof computeTileImportOutcome>,
  showErrorSnackbar: ShowErrorSnackbar
) => {
  if (!outcome.exceeded) return;

  const tilesLabel = `tile${outcome.tilesToAdd.length === 1 ? '' : 's'}`;
  showErrorSnackbar(
    `Tile limit of ${HIGHLIGHTED_PRICE_MAX_ITEMS} exceeded. Only ${outcome.tilesToAdd.length} ${tilesLabel} added.`,
    5000,
    'tiles-limit-exceeded-error'
  );
};

// Dispatches the right Redux action based on what editingTarget encodes.
// See DrawerStepsContext for the encoding scheme (null / appId-only / appId+tabId).
export const handleEditModeImport = ({
  actions,
  currentTabId,
  currentTiles,
  templateWidget,
  editingTarget,
  showErrorSnackbar,
  showSuccessSnackbar
}: {
  actions: EditActions;
  currentTabId: string;
  currentTiles: TilesWidgetV2['tiles'];
  templateWidget: MarketApp;
  editingTarget: { appId?: string; tabId?: string } | null;
  showErrorSnackbar: ShowErrorSnackbar;
  showSuccessSnackbar: ShowSuccessSnackbar;
}) => {
  // Tiles are merged into the highlighted-price row regardless of editingTarget.
  if (isTilesWidget(templateWidget)) {
    const outcome = computeTileImportOutcome(currentTiles, templateWidget.tiles);

    addTilesToDashboard(actions, currentTabId, outcome.tilesToAdd);
    notifyDuplicateTiles(outcome, showErrorSnackbar);
    notifyAddedTiles(outcome, showSuccessSnackbar);
    notifyExceededTiles(outcome, showErrorSnackbar);
    return;
  }

  if (isTabsWidget(templateWidget)) {
    // appId only → add the imported widget as a new tab inside the target widget.
    if (editingTarget?.appId && !editingTarget.tabId) {
      const newTab = templateWidget.tabs[0];
      if (newTab) {
        actions.addTabToApp(currentTabId, editingTarget.appId, newTab);
        return;
      }
    }
    // null editingTarget → create a new top-level widget on the current dashboard tab.
    actions.addApp(currentTabId, templateWidget);
  }
};
